import { useAppDispatch } from 'hooks';

import {
  focusAppointment,
  restoreViewState,
  storeViewState,
  unfocusAppointment,
} from '~weekly-planner/reducers/weeklyPlanner';

/**
 * Custom hook to handle focusing and unfocusing an appointment in the weekly planner.
 * focusAppointments view type, range type, date, focused worker, and local parameters as needed.
 *
 * @return focus - Function to focus or unfocus an appointment.
 */
export function useFocusAppointment() {
  const dispatch = useAppDispatch();

  /**
   * Focus or unfocus an appointment.
   *
   * @param {object} focused - The focused appointment object or null to unfocus.
   */
  const focus = (focused: any) => {
    if (focused?.appointment) {
      // Destructure and collect leftovers
      const { appointment, isBiddingView = false, newBidders = [] } = focused;

      // Prepare focused state
      const focusedState = {
        appointment: { ...appointment },
        isBiddingView,
        newBidders,
      };
      dispatch(storeViewState({}));
      dispatch(
        focusAppointment({
          // we are not allowing people to focus a worker and an appointment at the same time
          focusedWorkerId: null,
          focused: focusedState,
          params: {
            viewType: 'Users',
            rangeType: 'Daily',
            date: appointment.date ?? undefined,
            focus_appointment_id: appointment.id,
            focus_worker_id: null,
          },
        }),
      );
    } else {
      dispatch(unfocusAppointment({}));
    }
  };

  /**
   * Unfocus and restore previous view state.
   */
  const unfocusAndRestoreView = () => {
    dispatch(unfocusAppointment({}));
    dispatch(restoreViewState({}));
  };

  /**
   * Unfocus an appointment.
   */
  const unfocus = () => {
    dispatch(unfocusAppointment({}));
  };

  return { focus, unfocusAndRestoreView, unfocus };
}
